import React, { useCallback, useRef } from "react";
import { ChakraStylesConfig, MultiValueRemoveProps, Select, SelectInstance, StylesConfig, chakraComponents } from "chakra-react-select";
import { Box, BoxProps, useTheme } from "@chakra-ui/react";
import { useExclusiveMenu } from "../hooks/useExclusiveMenu";

export interface SelectorOption {
  value: string;
  label: string;
}

const MultiValueRemoveWithLabel = (props: MultiValueRemoveProps<SelectorOption, boolean>) => {
  const { label } = props.data;

  return (
    <chakraComponents.MultiValueRemove
      {...props}
      innerProps={{ ...props.innerProps, "aria-label": `Quitar ${label}` }}
    />
  );
};

// En selección múltiple cada valor se muestra como tag con su botón para quitarlo.
const selectComponents = { MultiValueRemove: MultiValueRemoveWithLabel };

// En selección múltiple el menú sigue abierto para elegir varios valores; sin quitar el
// foco, porque en equipos táctiles react-select lo quita al elegir y eso cierra el menú.
const keepMenuOpenOnSelect = { closeMenuOnSelect: false, blurInputOnSelect: false };

// Ancho por defecto: muestra completo el placeholder más largo ("Selecciona una sucursal")
// y no depende de los valores elegidos.
// Quien lo usa puede pasar su propio `width`; en pantallas angostas no rebasa la fila.
const DEFAULT_WIDTH = "17em";

// El control conserva el tamaño de un input aunque haya muchos tags: los tags siguen
// haciendo wrap, pero dentro del área de valores, que hace scroll vertical (funciona con
// la rueda del mouse, a diferencia de un scroll horizontal) en lugar de crecer.
const fixedHeightStyles: ChakraStylesConfig<SelectorOption, boolean> = {
  control: (provided) => ({
    ...provided,
    maxH: provided.minH,
    flexWrap: "nowrap",
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: "0.75rem",
    alignSelf: "stretch",
    minWidth: 0,
    overflowX: "hidden",
    overflowY: "auto",
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": { width: "4px" },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "gray.300", borderRadius: "full" },
  }),
};

export interface SelectorProps extends BoxProps {
  options: Array<SelectorOption>,
  isMulti?: boolean,
  onChange?: (value: any) => void,
  placeholder?: string,
  value?: any,
  isLoading?: boolean,
}

export const Selector = (props: SelectorProps) => {

  const { options, isMulti, onChange, placeholder, value, isLoading, ...boxProps } = props;
  const { zIndices } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<SelectInstance<SelectorOption, boolean>>(null);

  // El menú vive en un portal en document.body, fuera del contenedor del control.
  const isInsideSelector = useCallback((target: Node) =>
    Boolean(containerRef.current?.contains(target) || selectRef.current?.menuListRef?.contains(target)), []);
  const menu = useExclusiveMenu(isInsideSelector);

  // El portal se monta en body: el zIndex lo deja por encima de los demás controles.
  const menuStyles: StylesConfig<SelectorOption, boolean> = {
    menuPortal: (provided) => ({ ...provided, zIndex: zIndices.popover }),
  };

  const mappedValue = isMulti
    ? options.filter(option => (value || []).includes(option.value))
    : options.find(option => option.value === value);

  const handleChange = (selectedOption: any) => {
    if (!onChange) {
      return;
    }
    if (isMulti) {
      const values = selectedOption.map((option: any) => option.value);

      onChange(values);
    } else {
      onChange(selectedOption?.value);
    }
  };

  return (
    <Box ref={containerRef} width={DEFAULT_WIDTH} maxWidth="100%" {...boxProps}>
      <Select
        ref={selectRef}
        menuIsOpen={menu.isOpen}
        onMenuOpen={menu.open}
        onMenuClose={menu.close}
        {...(isMulti && keepMenuOpenOnSelect)}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement="auto"
        styles={menuStyles}
        isLoading={isLoading}
        options={options}
        isMulti={isMulti}
        tagColorScheme="orange"
        tagVariant="solid"
        onChange={(option: any) => handleChange(option)}
        components={selectComponents}
        placeholder={placeholder || "Selecciona opciones..."}
        value={mappedValue}
        chakraStyles={fixedHeightStyles}
      />
    </Box>
  );
};
